import { Alert } from "@porchpass/ui";
import type { DtoApplicationProgressResp } from "@porchpass/api-client";

export default ({
  formData,
}: {
  formData: Partial<DtoApplicationProgressResp>;
}) => {
  const { application_status, borrower, co_borrowers } = formData;
  const isOnlyBorrower = !formData.co_borrowers?.length;
  const areAllCoBorrowersInStatus = (status: string): boolean => {
    return (
      Array.isArray(co_borrowers) &&
      co_borrowers.length > 0 &&
      co_borrowers?.every((x) => x.status === status)
    );
  };

  // Application status
  const isApplicationStarted = application_status === "STARTED";
  const isApplicationSentForSignature =
    application_status === "SENT_FOR_SIGNATURE";
  const isApplicationWaitingOnSignatureService =
    application_status === "WAITING_ON_ESIGNATURE_SERVICE";
  const isApplicationSubmitted = application_status === "SUBMITTED";
  const isApplicationReviewed = application_status === "REVIEWED";

  // Borrower and co-borrower statuses and render logic.
  const isBorrowerInProgress = borrower?.status === "IN_PROGRESS";
  const areCoBorrowersInProgress = co_borrowers?.some(
    (x) => x.status === "IN_PROGRESS",
  );
  const areAnyInProgress = isBorrowerInProgress || areCoBorrowersInProgress;

  const isBorrowerReadyToSign = borrower?.status === "READY_TO_SIGN_SUBMIT";
  const areCoBorrowersReadyToSign = areAllCoBorrowersInStatus(
    "READY_TO_SIGN_SUBMIT",
  );
  const areAnyReadyToSign = isBorrowerReadyToSign || areCoBorrowersReadyToSign;

  const isBorrowerWaiting =
    borrower?.status === "WAITING_ON_ESIGNATURE_SERVICE";
  const areCoBorrowersWaiting = areAllCoBorrowersInStatus(
    "WAITING_ON_ESIGNATURE_SERVICE",
  );
  const areAnyWaiting = isBorrowerWaiting || areCoBorrowersWaiting;

  const isBorrowerSigned = borrower?.status === "SIGNED";
  const areCoBorrowersSigned = areAllCoBorrowersInStatus("SIGNED");
  const areAnySigned = isBorrowerSigned || areCoBorrowersSigned;

  const isWaiting =
    (isOnlyBorrower && isBorrowerWaiting) ||
    (isBorrowerWaiting && areCoBorrowersWaiting);
  const isApplicationSigned =
    (isOnlyBorrower && isBorrowerSigned) ||
    (isBorrowerSigned && areCoBorrowersSigned);

  // Exit early if either the borrower or co-borrowers are in progress
  if (areAnyInProgress) {
    return (
      <Alert variant="info" title="Welcome back!">
        Click the icon next to the section you last worked on to continue your
        application.
      </Alert>
    );
  }

  // Main render logic
  if (isApplicationReviewed) {
    return (
      <Alert
        variant="info"
        title="Congratulations on submitting your application!"
      >
        A loan officer is reviewing your application. We&apos;ll reach out soon
        with details on how to proceed.
      </Alert>
    );
  }

  if (isApplicationStarted) {
    if (areAnyReadyToSign) {
      return (
        <Alert variant="info" title="Time to sign!">
          To submit your application, tap below to review and sign your
          documents.
        </Alert>
      );
    }
  }

  if (isApplicationSentForSignature) {
    if (isWaiting || (areAnySigned && areAnyWaiting)) {
      return (
        <Alert variant="info" title="Thanks for signing!">
          Give us a moment to save and submit your application.
        </Alert>
      );
    }

    if (
      (areAnyWaiting && areAnyReadyToSign) ||
      (areAnySigned && !areAnyWaiting)
    ) {
      return (
        <Alert variant="info" title="Time to sign!">
          To submit your application, tap below to review and sign your
          documents.
        </Alert>
      );
    }
  }

  if (isApplicationWaitingOnSignatureService) {
    return (
      <Alert variant="info" title="Thanks for signing!">
        Give us a moment to save and submit your application.
      </Alert>
    );
  }

  if (isApplicationSubmitted) {
    if (isApplicationSigned) {
      return (
        <Alert variant="success" title="Thanks for submitting!">
          We will start reviewing your application soon. Until then, you can
          still make changes to your submission by using the edit icons below.
        </Alert>
      );
    }
  }

  return (
    <Alert variant="info" title="Welcome back!">
      Click the icon next to the section you last worked on to continue your
      application.
    </Alert>
  );
};
